import React, { ReactNode, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useCategoryContext, Category } from '../context/CategoryContext';
import CartSlider from './CartSlider';
import ProfileSlider from './ProfileSlider';
import ImageSlider from './ImageSlider';
import Footer from './Footer';
import HomeIcon from '../Assets/icons/HomeIcon';
import CategoriesIcon from '../Assets/icons/CategoriesIcon';
import CartIcon from '../Assets/icons/CartIcon';
import UserIcon from '../Assets/icons/UserIcon';
import LogoutIcon from '../Assets/icons/LogoutIcon';
import '../App.css';
import './UserLayout.css';
import { getMostSaleProducts, getAllCompanies, getCompanyLogoLocal, CompanySummary } from '../api/productApi';
import { showCustomToast } from './CustomToast';
import LoadingButton from './LoadingButton';
import { resolveImageUrl } from '../utils/image';
import ProductImageModal from './ProductImageModal';
import ProductVideoModal from './ProductVideoModal';
import { updatePageSEO } from '../utils/seo';

interface UserLayoutProps {
  children: ReactNode;
  user: any;
  setUser: (user: any) => void;
  hideSlider?: boolean;
}

export default function UserLayout({ user, setUser, children, hideSlider }: UserLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, cartCount, addToCart } = useCart();
  const { categories } = useCategoryContext();
  const [cartOpen, setCartOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [mostSale, setMostSale] = useState<any[]>([]);
  const [companies, setCompanies] = useState<CompanySummary[]>([]);
  const [adding, setAdding] = useState<{ [key: string]: boolean }>({});
  const [imageModal, setImageModal] = useState<{ isOpen: boolean; images: string[]; index: number; productName: string }>({
    isOpen: false,
    images: [],
    index: 0,
    productName: ''
  });
  const [videoModal, setVideoModal] = useState<{ isOpen: boolean; videoUrl: string; productName: string }>({
    isOpen: false,
    videoUrl: '',
    productName: ''
  });

  const handleProfileClick = () => setProfileOpen(true);

  const navigateTo = (path: string, state?: any) => {
    if (state !== undefined) {
      navigate(path, { state });
    } else {
      navigate(path);
    }
    window.scrollTo(0, 0);
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
  };

  const handleProfileNavigate = (path: string) => {
    setProfileOpen(false);
    if (path === '/logout') {
      localStorage.clear();
      setUser(null);
      navigateTo('/');
    } else {
      navigateTo(path);
    }
  };

  const handleCategorySelect = (categoryId: number, categoryName: string) => {
    navigateTo(`/${categoryName.toLowerCase().replace(/\s+/g, '-')}`, { categoryId });
  };

  const handleCompanySelect = (companyName: string, companyLogoUrl?: string) => {
    const slug = encodeURIComponent(companyName.toLowerCase().trim().replace(/\s+/g, '-'));
    navigateTo(`/company/${slug}`, { companyName, companyLogoUrl });
  };

  const handleImageClick = (images: string[], index: number, productName: string) => {
    setImageModal({
      isOpen: true,
      images: images || [],
      index: index || 0,
      productName
    });
  };

  const closeImageModal = () => {
    setImageModal({
      isOpen: false,
      images: [],
      index: 0,
      productName: ''
    });
  };

  useEffect(() => {
    updatePageSEO({
      title: 'Jay Bhavani Fatakda | Jai Bhavani Fireworks - Buy Firecrackers Online Wholesale (જય ભવાની ફટાકડા સ્ટોર)',
      description: 'Jay Bhavani Fatakda (Jai Bhavani Fireworks / જય ભવાની ફટાકડા સ્ટોર) - Best online fatakda store for wholesale firecrackers & Sivakasi crackers in Gujarat & India. Buy Diwali fatakda, rockets, sparklers, sky shots & green crackers at direct factory wholesale rates on jaibhavani.store.',
      canonicalPath: '/',
      keywords: 'jay bhavani, jaibhavani, jaybhavani, jai bhavani, jay bhavani fatakda, jaibhavani fatakda, jaybhavani fatakda, fatakda, firecrackers, buy firecrackers online, wholesale firecrackers, sivakasi firecrackers, diwali fatakda, fatakda online shopping, green crackers online'
    });

    (async () => {
      try {
        const data = await getMostSaleProducts();
        setMostSale(Array.isArray(data) ? data : data?.value || []);
      } catch {
        setMostSale([]);
      }
      try {
        const compList = await getAllCompanies();
        setCompanies(compList || []);
      } catch {
        setCompanies([]);
      }
    })();
  }, []);

  const bannerImages = [
    require('../Assets/JayBhavaniFatakdaBanner.webm'),
    require('../Assets/FireworksHeroBanner.jpg'),
    require('../Assets/FireworksWholesaleBanner.jpg'),
    require('../Assets/Diwali-Offer-4.jpg')
  ];

  const currentPath = location.pathname;

  return (
    <div className="ul-wrapper">
      {/* Top Announcement Bar */}
      <div className="ul-announcement-bar">
        <div className="ul-announcement-content">
          <span>⚖️ <strong>Supreme Court 2018 Compliant:</strong> Catalogue &amp; Offline Booking Only</span>
          <span className="ul-announcement-divider">|</span>
          <span>✨ <strong>Festive Wholesale Sale:</strong> Flat 50% Off | <strong>Min. Order ₹2,000</strong></span>
          <span className="ul-announcement-divider">|</span>
          <a 
            href="https://wa.me/917990696364?text=Hello%20Jai%20Bhavani%20Fireworks,%20I%20want%20to%20inquire%20about%20crackers" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="ul-announcement-wa"
          >
            💬 WhatsApp: +91 79906 96364
          </a>
        </div>
      </div>

      {/* Main Glassmorphism Header */}
      <header className="ul-header">
        <div className="ul-header-container">
          <div className="ul-header__brand" onClick={() => navigateTo('/')} role="button" tabIndex={0}>
            <div className="ul-brand-logo-wrap">
              <img 
                src={require('../Assets/WebsiteLogo.png')} 
                alt="Jai Bhavani Fireworks" 
                className="ul-brand__logo"
              />
            </div>
            <div className="ul-brand-text">
              <h1 className="ul-brand__title" style={{ margin: 0, fontSize: 'inherit', fontWeight: 'inherit', lineHeight: 'inherit' }}>
                <span style={{ display: 'block' }}>જય ભવાની ફટાકડા સ્ટોર</span>
                <span className="ul-brand__subtitle" style={{ display: 'block' }}>Jai Bhavani Fatakada Store</span>
              </h1>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="ul-header__nav" aria-label="Primary">
            <a 
              className={`ul-nav__link ${currentPath === '/' ? 'active' : ''}`} 
              href="/"
              onClick={(e) => { e.preventDefault(); navigateTo('/'); }}
            >
              <HomeIcon size={18} />
              <span>Home</span>
            </a>
            <a 
              className={`ul-nav__link ${currentPath === '/category' ? 'active' : ''}`} 
              href="/category"
              onClick={(e) => { e.preventDefault(); navigateTo('/category'); }}
            >
              <CategoriesIcon size={18} />
              <span>All Categories</span>
            </a>
            <a 
              className={`ul-nav__link ${currentPath === '/my-orders' || currentPath === '/orders' ? 'active' : ''}`} 
              href="/my-orders"
              onClick={(e) => { e.preventDefault(); navigateTo('/my-orders'); }}
            >
              <span>My Orders</span>
            </a>
          </nav>

          {/* Header Action Buttons */}
          <div className="ul-header__actions">
            <a 
              href="https://wa.me/917990696364?text=Hello%20Jai%20Bhavani%20Fireworks,%20I%20need%20assistance" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="ul-btn-wa-header"
              title="Chat on WhatsApp"
            >
              <span className="wa-dot"></span>
              <span className="wa-text">Chat Support</span>
            </a>

            <button 
              className="ul-nav__cart-btn" 
              onClick={() => setCartOpen(true)} 
              aria-label={`Open cart with ${cartCount} items`}
            >
              <div className="ul-cart-icon-wrap">
                <CartIcon size={20} />
                {cartCount > 0 && <span className="ul-cart-badge">{cartCount}</span>}
              </div>
              <span className="ul-cart-text">Cart</span>
            </button>

            <button 
              className="ul-user__trigger" 
              onClick={handleProfileClick} 
              aria-haspopup="dialog" 
              aria-controls="profile-slider"
              title="My Account"
            >
              <div className="ul-user-avatar">
                <UserIcon size={18} />
              </div>
              <span className="ul-user__name">{user ? (user.name || user.userName || 'Account') : 'Login / Account'}</span>
            </button>

            {user && (
              <button 
                className="ul-logout-btn" 
                onClick={() => handleProfileNavigate('/logout')}
                title="Sign out"
              >
                <LogoutIcon size={18} />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="ul-main">
        {/* Horizontal Category Scroll Bar (Always available or on home) */}
        {categories && categories.length > 0 && (
          <div className="ul-cat-scroll-container">
            <div className="ul-cat-scroll-track">
              <button 
                className={`ul-cat-pill ${currentPath === '/category' ? 'active' : ''}`}
                onClick={() => navigateTo('/category')}
              >
                <span className="ul-cat-pill-icon">✨</span>
                <span>All Categories</span>
              </button>
              {categories.map((cat: Category) => (
                <button
                  key={cat.categoryID}
                  className="ul-cat-pill"
                  onClick={() => handleCategorySelect(cat.categoryID, cat.categoryName)}
                >
                  <span className="ul-cat-pill-icon">🧨</span>
                  <span>{cat.categoryName}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <section className="ul-hero">
          {!hideSlider && (
            <div className="ul-hero-section">
              {/* Banner Slider */}
              <div className="ul-hero-banner-container">
                <ImageSlider images={bannerImages} />
              </div>

              {/* Category Showcase Gallery - directly below banner photo */}
              <section className="ul-cat-gallery" aria-label="Explore Categories">
                <div className="ul-cat-grid">
                  {categories.map((cat: Category) => (
                    <button
                      key={cat.categoryID}
                      className="ul-cat-card"
                      onClick={() => handleCategorySelect(cat.categoryID, cat.categoryName)}
                      aria-label={`Browse ${cat.categoryName}`}
                      title={cat.categoryName}
                    >
                      <div className="ul-cat-card__media">
                        {(cat as any).imageUrl ? (
                          <img 
                            src={resolveImageUrl((cat as any).imageUrl)} 
                            alt={cat.categoryName}
                            className="ul-cat-card__image"
                            loading="lazy"
                          />
                        ) : (
                          <div className="ul-cat-card__placeholder">
                            <span className="ul-cat-card__placeholder-icon">🧨</span>
                            <span className="ul-cat-card__placeholder-name">{cat.categoryName}</span>
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </section>

              {/* Shop by Company / Brand Showcase */}
              {companies.length > 0 && (
                <section className="ul-company-showcase" aria-label="Shop by Company">
                  <div className="ul-section-header">
                    <div>
                      <span className="ul-section-tag">TOP BRANDS</span>
                      <h2 className="ul-section-title">Shop by Company / Brand</h2>
                    </div>
                    <span className="ul-section-subtitle-badge">Browse Brand Catalog</span>
                  </div>

                  <div className="ul-company-grid">
                    {companies.map((comp) => {
                      const logoSrc = comp.companyLogoUrl || comp.sampleImageUrl;
                      return (
                        <button
                          key={comp.companyName}
                          type="button"
                          className="ul-company-card"
                          onClick={() => handleCompanySelect(comp.companyName, comp.companyLogoUrl)}
                          title={`View all ${comp.companyName} fireworks`}
                        >
                          <div className="ul-company-card__icon">
                            {logoSrc ? (
                              <img
                                src={resolveImageUrl(logoSrc)}
                                alt={comp.companyName}
                                loading="lazy"
                                decoding="async"
                                onError={(e) => {
                                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            ) : (
                              <span>🏢</span>
                            )}
                          </div>
                          <div className="ul-company-card__info">
                            <h3 className="ul-company-card__name">{comp.companyName}</h3>
                            <span className="ul-company-card__meta">
                              {comp.productCount} {comp.productCount === 1 ? 'Product' : 'Products'}
                              {comp.categoryCount ? ` • ${comp.categoryCount} Categories` : ''}
                            </span>
                          </div>
                          <span className="ul-company-card__arrow">&rarr;</span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* Value Proposition / Trust Features Strip - moved after category listing */}
              <div className="ul-trust-strip">
                <div className="ul-trust-item">
                  <div className="ul-trust-icon">🛡️</div>
                  <div className="ul-trust-text">
                    <strong>100% Legal Store</strong>
                    <span>Explosives Act licensed</span>
                  </div>
                </div>
                <div className="ul-trust-item">
                  <div className="ul-trust-icon">🧨</div>
                  <div className="ul-trust-text">
                    <strong>Factory Direct</strong>
                    <span>Wholesale rates (Min. ₹2,000)</span>
                  </div>
                </div>
                <div className="ul-trust-item">
                  <div className="ul-trust-icon">🌿</div>
                  <div className="ul-trust-text">
                    <strong>Green Crackers</strong>
                    <span>Certified eco-friendly formulas</span>
                  </div>
                </div>
                <div className="ul-trust-item">
                  <div className="ul-trust-icon">💬</div>
                  <div className="ul-trust-text">
                    <strong>WhatsApp Enquiries</strong>
                    <span>Instant stock verification</span>
                  </div>
                </div>
              </div>

              {/* Top Deals / Best Sellers Section */}
              {mostSale.length > 0 && (
                <section className="ul-most-sale" aria-label="Best Selling Deals">
                  <div className="ul-section-header">
                    <div>
                      <span className="ul-section-tag highlight">HOT DEALS</span>
                      <h2 className="ul-section-title">Best Selling Fireworks</h2>
                    </div>
                    <span className="ul-section-subtitle-badge">Festive Wholesale Rates</span>
                  </div>

                  <div className="ul-most-grid">
                    {mostSale.slice(0, 8).map((p: any) => {
                      const currentPrice = Number(p.price || p.productRate || 0);
                      const mrpPrice = currentPrice > 0 ? currentPrice * 2 : 0;
                      const rawImages = p.imageUrls && p.imageUrls.length > 0 ? p.imageUrls : (p.imageUrl ? [p.imageUrl] : []);
                      const allImages = rawImages.map((r: string) => resolveImageUrl(r)).filter(Boolean) as string[];
                      const first = allImages.length > 0 ? allImages[0] : '';
                      const imageUrl = first || resolveImageUrl(p.productImageUrl);
                      const isAdded = Array.isArray(cart) && cart.some(i => String(i.id) === String(p.productId));
                      const key = String(p.productId);
                      const targetVideoUrl = p.videoUrl || p.VideoUrl;
                      const prodCompany = p.companyName || p.CompanyName;
                      const prodCompanyLogo = p.companyLogoUrl || p.CompanyLogoUrl || (prodCompany ? getCompanyLogoLocal(prodCompany) : '');

                      return (
                        <div key={p.productId} className="ul-prod-card">
                          <div className="ul-prod-card-top">
                            <span className="ul-discount-tag">50% OFF</span>
                            <div 
                              className="ul-prod-media"
                              onClick={() => handleImageClick(allImages, 0, p.productName)}
                            >
                              {first ? (
                                <img
                                  src={first}
                                  alt={p.productName}
                                  loading="lazy"
                                  onError={(e) => { 
                                    const t = e.currentTarget as HTMLImageElement; 
                                    t.style.display='none'; 
                                  }}
                                />
                              ) : (
                                <div className="ul-prod-placeholder">
                                  <span>🎆</span>
                                </div>
                              )}
                              <span className="ul-zoom-hint">🔍 Zoom</span>
                            </div>
                          </div>

                          <div className="ul-prod-meta">
                            {prodCompany && (
                              <div>
                                <button
                                  type="button"
                                  className="ul-prod-company-badge"
                                  onClick={() => handleCompanySelect(prodCompany, prodCompanyLogo)}
                                  title={`View all ${prodCompany} products`}
                                >
                                  {prodCompanyLogo ? (
                                    <img
                                      src={resolveImageUrl(prodCompanyLogo)}
                                      alt={prodCompany}
                                      className="ul-company-badge-logo"
                                      onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                                    />
                                  ) : (
                                    <span>🏢</span>
                                  )}
                                  <span>{prodCompany}</span>
                                </button>
                              </div>
                            )}
                            <h3 className="ul-prod-name" title={p.productName}>{p.productName}</h3>
                            <div className="ul-prod-prices">
                              {mrpPrice > 0 && (
                                <span className="ul-prod-mrp">₹{mrpPrice.toFixed(2)}</span>
                              )}
                              <span className="ul-prod-price">₹{currentPrice.toFixed(2)}</span>
                              <span className="ul-prod-saving">Save ₹{(mrpPrice - currentPrice).toFixed(0)}</span>
                            </div>

                            {targetVideoUrl && (
                              <button
                                type="button"
                                className="ul-btn-watch-video"
                                onClick={() => setVideoModal({
                                  isOpen: true,
                                  videoUrl: targetVideoUrl,
                                  productName: p.productName,
                                })}
                                title="Watch live cracker demo video"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                </svg>
                                <span>Watch Demonstration</span>
                              </button>
                            )}

                            <LoadingButton
                              onClick={async () => {
                                setAdding(prev => ({ ...prev, [key]: true }));
                                try {
                                  await addToCart({
                                    id: key,
                                    name: p.productName,
                                    price: currentPrice,
                                    imageUrl,
                                    quantity: 1,
                                  });
                                  const currentCartCount = cart.length + 1;
                                  showCustomToast(`✓ Added "${p.productName}" to cart! (${currentCartCount} items)`, 'success', 2000);
                                } finally {
                                  setAdding(prev => ({ ...prev, [key]: false }));
                                }
                              }}
                              loading={adding[key] || false}
                              className={`ul-add-cart-btn ${isAdded ? 'added' : ''}`}
                              disabled={isAdded}
                            >
                              {isAdded ? '✓ Added to Cart' : '🛒 Add to Cart'}
                            </LoadingButton>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}
            </div>
          )}
        </section>

        {children}
      </main>

      {/* Floating WhatsApp Action Button */}
      <a 
        href="https://wa.me/917990696364?text=Hello%20Jai%20Bhavani%20Fireworks,%20I%20want%20to%20order%20crackers" 
        target="_blank" 
        rel="noopener noreferrer" 
        className="ul-floating-wa"
        aria-label="Direct WhatsApp Order"
        title="Direct WhatsApp Order"
      >
        <div className="wa-pulse"></div>
        <svg viewBox="0 0 32 32" width="28" height="28" fill="#fff">
          <path d="M16 2a13.9 13.9 0 0 0-12 21L2 30l7.3-1.9A13.9 13.9 0 1 0 16 2zm0 25.5a11.5 11.5 0 0 1-5.9-1.6l-.4-.3-4.4 1.1 1.2-4.3-.3-.4A11.6 11.6 0 1 1 16 27.5zm6.4-8.7c-.4-.2-2.1-1-2.4-1.1-.3-.2-.6-.2-.8.2s-1 1.1-1.2 1.3-.4.2-.7 0a9.2 9.2 0 0 1-2.7-1.7 10.2 10.2 0 0 1-1.9-2.3c-.2-.4 0-.6.1-.8l.5-.6c.2-.2.2-.4.3-.6.1-.2 0-.4 0-.6s-.8-2-1.1-2.7c-.3-.7-.6-.6-.8-.6h-.7c-.2 0-.7.1-1.1.5s-1.5 1.5-1.5 3.6 1.5 4.2 1.7 4.5c.3.3 3 4.6 7.3 6.4 1 .4 1.8.7 2.4.9 1 .3 2 .3 2.7.2.9-.1 2.6-1.1 3-2.1.4-1.1.4-2 .3-2.1-.2-.2-.5-.3-.9-.5z"/>
        </svg>
        <span className="floating-wa-label">Order on WhatsApp</span>
      </a>

      {/* Mobile Sticky Bottom Navigation Bar */}
      <nav className="ul-mobile-bottom-nav" aria-label="Mobile Navigation">
        <button 
          className={`ul-mobile-nav-item ${currentPath === '/' ? 'active' : ''}`}
          onClick={() => navigateTo('/')}
        >
          <HomeIcon size={20} />
          <span>Home</span>
        </button>
        <button 
          className={`ul-mobile-nav-item ${currentPath === '/category' ? 'active' : ''}`}
          onClick={() => navigateTo('/category')}
        >
          <CategoriesIcon size={20} />
          <span>Categories</span>
        </button>
        <button 
          className="ul-mobile-nav-item ul-mobile-cart-btn"
          onClick={() => setCartOpen(true)}
        >
          <div className="mobile-cart-badge-wrap">
            <CartIcon size={22} />
            {cartCount > 0 && <span className="mobile-badge">{cartCount}</span>}
          </div>
          <span>Cart</span>
        </button>
        <button 
          className={`ul-mobile-nav-item ${currentPath === '/my-orders' ? 'active' : ''}`}
          onClick={() => navigateTo('/my-orders')}
        >
          <span className="mobile-nav-emoji">📦</span>
          <span>Orders</span>
        </button>
        <button 
          className={`ul-mobile-nav-item ${profileOpen ? 'active' : ''}`}
          onClick={handleProfileClick}
        >
          <UserIcon size={20} />
          <span>{user ? 'Account' : 'Login'}</span>
        </button>
      </nav>

      <Footer />

      {profileOpen && (
        <ProfileSlider
          open={profileOpen}
          onClose={() => setProfileOpen(false)}
          onNavigate={handleProfileNavigate}
          user={user}
        />
      )}
      <CartSlider open={cartOpen} onClose={() => setCartOpen(false)} />
      
      <ProductImageModal
        isOpen={imageModal.isOpen}
        onClose={closeImageModal}
        images={imageModal.images}
        startIndex={imageModal.index}
        productName={imageModal.productName}
      />

      <ProductVideoModal
        isOpen={videoModal.isOpen}
        onClose={() => setVideoModal({ isOpen: false, videoUrl: '', productName: '' })}
        videoUrl={videoModal.videoUrl}
        productName={videoModal.productName}
      />
    </div>
  );
}
